import getBrandImage from '@/features/cars/helpers/getBrandImage';
import getModelsImage from '@/features/cars/helpers/getModelsImage';
import { FilterOptionType, FilterRecordType } from '@/features/filters/types';
import { FilterBlocksEnum } from '@/interfaces/constructor.interfaces';
import type {
  FilterValuesType,
  IFilterColor,
  IFilterLocation,
  IFilterModel,
  IFilterSpecification,
} from '@/interfaces/filters.interfaces';
import { LocationTypeEnum } from '@/interfaces/locations';
import { ISettings } from '@/interfaces/settings.interfaces';
import { II18Service } from '@/services/I18Service';
import InjectContainer from '@/services/InjectContainer';
import ServiceTypes from '@/services/ServiceTypes';
import compactArray from '@/utils/compactArray';
import instanceOfFilterPrimitive from '@/utils/instanceOfFilterPrimitive';
import instanceOfFilterRange from '@/utils/instanceOfFilterRange';
import instanceOfReference from '@/utils/instanceOfReference';

import filterDisabledOptions from './filterDisabledOptions';
import sortCheckboxOptions from './sortCheckboxOptions';
import sortSelectOptions from './sortSelectOptions';

type ParamsType = {
  type: FilterBlocksEnum;
  props?: Record<string, unknown>;
  filterValues?: FilterValuesType;
};

type PrepareFilterItemPropsFunctionType = {
  (params: ParamsType, setting: ISettings): Record<string, unknown>;
};

type OptionLabelFormatterFunctionType = {
  (option: FilterOptionType): string;
};

const optionLabelFormatter = (record: FilterRecordType): string | null => {
  if (instanceOfReference(record)) {
    let label: string | null = record.displayName ?? record.name ?? null;

    if ('color' in record) {
      const colorRecord = record as IFilterColor;
      label = label
        ? `${label}${
            !!colorRecord.additionalColorName && !!record.additionalColor
              ? ` / ${colorRecord.additionalColorName}`
              : ''
          }`
        : null;
    }
    if ('specification' in record) {
      const specificationRecord = record as IFilterSpecification;
      let specificationLabel: string | null = null;

      if (typeof specificationRecord.stringValue === 'string') {
        specificationLabel = specificationRecord.stringValue;
      }

      if (typeof specificationRecord.numberValue === 'number') {
        specificationLabel = String(specificationRecord.numberValue);
      }

      if (typeof specificationRecord.booleanValue === 'boolean') {
        const { i18n } = InjectContainer.get<II18Service>(ServiceTypes.I18N);

        specificationLabel = specificationRecord.booleanValue
          ? i18n.t('booleanValues.trueValue')
          : i18n.t('booleanValues.falseValue');
      }

      label ??= specificationLabel;
    }

    return label;
  }
  if (instanceOfFilterPrimitive(record)) {
    if (typeof record.displayName !== 'undefined') {
      return record.displayName;
    }

    const { i18n } = InjectContainer.get<II18Service>(ServiceTypes.I18N);

    if (typeof record.value === 'boolean') {
      return record
        ? i18n.t('booleanValues.trueValue')
        : i18n.t('booleanValues.falseValue');
    }

    return String(record.value);
  }
  return null;
};

const optionFormatterMap: Record<
  FilterBlocksEnum,
  (record: FilterRecordType, setting: ISettings) => FilterOptionType | undefined
> = {
  [FilterBlocksEnum.FilterSelect](record) {
    const label = optionLabelFormatter(record);

    if (!label) {
      return;
    }

    if (instanceOfReference(record)) {
      return {
        label,
        value: record.id,
        disabled: !record.available,
        order: record.position,
      };
    }
    if (instanceOfFilterPrimitive(record)) {
      return {
        label,
        value: record.value,
        disabled: !record.available,
      };
    }
  },
  [FilterBlocksEnum.FilterMultipleSelect](record) {
    const label = optionLabelFormatter(record);

    if (!label) {
      return;
    }

    if (instanceOfReference(record)) {
      return {
        label,
        value: record.id,
        disabled: !record.available,
        order: record.position,
      };
    }
    if (instanceOfFilterPrimitive(record)) {
      return {
        label,
        value: record.value,
        disabled: !record.available,
      };
    }
  },
  [FilterBlocksEnum.FilterLocation](record) {
    const label = optionLabelFormatter(record);

    if (!label) {
      return;
    }

    if (instanceOfReference(record)) {
      const locationRecord = record as IFilterLocation;
      return {
        label,
        value: locationRecord.id,
        disabled: !locationRecord.available,
        order:
          locationRecord.type === LocationTypeEnum.Cluster
            ? (locationRecord.position || 0) - 1000
            : locationRecord.position,
        type: locationRecord.type ?? LocationTypeEnum.City,
        clusterId: locationRecord?.cluster?.id,
      };
    }
  },
  [FilterBlocksEnum.FilterCheckboxGroup](record) {
    const label = optionLabelFormatter(record);

    if (!label) {
      return;
    }

    if (instanceOfReference(record)) {
      return {
        label,
        value: record.id,
        disabled: !record.available,
        order: record.position,
      };
    }
    if (instanceOfFilterPrimitive(record)) {
      return {
        label,
        value: record.value,
        disabled: !record.available,
      };
    }
  },
  [FilterBlocksEnum.FilterModelCheckboxGroup](record, setting) {
    const label = optionLabelFormatter(record);

    if (!label) {
      return;
    }

    if (instanceOfReference(record)) {
      const { i18n } = InjectContainer.get<II18Service>(ServiceTypes.I18N);

      const modelRecord = record as IFilterModel;

      // Если была рассчитана минимальная цена, то берем ее,
      // в ином случае берем маркетинговую цену
      const price =
        typeof modelRecord.price !== 'undefined'
          ? modelRecord.price
          : modelRecord.marketingPrice;

      const modelImage = modelRecord?.marketingMedia
        ? getModelsImage(modelRecord.marketingMedia, { media: setting.media })
        : null;

      const brandName =
        modelRecord.extendedBrand?.displayName || modelRecord.extendedBrand?.name;
      const brandImage = modelRecord?.brandMedia
        ? getBrandImage(modelRecord.brandMedia, { media: setting.media })
        : null;

      return {
        label,
        value: modelRecord.id,
        disabled: !modelRecord.available,
        order: modelRecord.position,
        model: {
          name: label,
          description: modelRecord.marketingPositioning,
          price: price
            ? `${i18n.t('modelCheckbox.from')} ${i18n.t('currency', { value: price })}`
            : undefined,
          imgAlt: modelRecord.name,
          imgSrc: modelImage?.original,
          imgSizes: modelImage?.sizes,
          imgSrcSet: modelImage?.srcSet,
        },
        brand: modelRecord.extendedBrand
          ? {
              id: modelRecord.extendedBrand.id,
              title: brandName,
              position: modelRecord.extendedBrand.position,
              description: modelRecord.brandPositioning,
              imgSrc: brandImage?.original,
              imgAlt: brandName,
            }
          : undefined,
      };
    }
  },
  [FilterBlocksEnum.FilterColorPicker](record) {
    const label = optionLabelFormatter(record);

    if (!label) {
      return;
    }

    if (instanceOfReference(record)) {
      const colorRecord = record as IFilterColor;

      return {
        label,
        value: colorRecord.id,
        color: colorRecord.color,
        disabled: !colorRecord.available,
        order: colorRecord.position,
      };
    }
  },
  [FilterBlocksEnum.FilterDuoColorPicker](record) {
    const label = optionLabelFormatter(record);

    if (!label) {
      return;
    }

    if (instanceOfReference(record)) {
      const colorRecord = record as IFilterColor;

      return {
        label,
        value: colorRecord.id,
        color: colorRecord.color,
        additionalColor: colorRecord.additionalColor ? colorRecord.additionalColor : null,
        disabled: !colorRecord.available,
        order: colorRecord.position,
      };
    }
  },
  [FilterBlocksEnum.FilterRangeSlider]() {
    return undefined;
  },
  [FilterBlocksEnum.FilterSlider]() {
    return undefined;
  },
};

/**
 * Получить свойства для фильтра с учетом
 * типа фильтра
 */
const prepareFilterItemProps: PrepareFilterItemPropsFunctionType = (
  { type, filterValues, props },
  setting,
) => {
  const { i18n } = InjectContainer.get<II18Service>(ServiceTypes.I18N);

  const preparedProps = { ...props };

  switch (type) {
    case FilterBlocksEnum.FilterLocation:
    case FilterBlocksEnum.FilterSelect:
    case FilterBlocksEnum.FilterMultipleSelect: {
      if (!preparedProps?.options && !!filterValues) {
        const resolvedValues = [filterValues].flat();
        Object.assign(preparedProps, {
          options: compactArray(
            resolvedValues.map(value => optionFormatterMap[type](value, setting)),
          ),
        });
      }
      if (Array.isArray(preparedProps.options) && !!preparedProps?.optionLabelFormatter) {
        const optionLabelFormatter =
          preparedProps.optionLabelFormatter as OptionLabelFormatterFunctionType;
        Object.assign(preparedProps, {
          options: preparedProps.options.map(option => ({
            ...option,
            label: optionLabelFormatter(option),
          })),
        });
        delete preparedProps.optionLabelFormatter;
      }
      if (Array.isArray(preparedProps.options) && !!preparedProps?.hideDisabledOptions) {
        Object.assign(preparedProps, {
          options: preparedProps.options.filter(filterDisabledOptions),
        });
        delete preparedProps.hideDisabledOptions;
      }
      return {
        placeholder: i18n.t('select.placeholder'),
        filterSort: sortSelectOptions,
        ...preparedProps,
      };
    }
    case FilterBlocksEnum.FilterCheckboxGroup:
    case FilterBlocksEnum.FilterModelCheckboxGroup:
    case FilterBlocksEnum.FilterColorPicker: {
      if (!preparedProps?.options && !!filterValues) {
        const resolvedValues = [filterValues].flat();
        Object.assign(preparedProps, {
          options: compactArray(
            resolvedValues.map(value => optionFormatterMap[type](value, setting)),
          ),
        });
      }
      if (Array.isArray(preparedProps.options) && !!preparedProps?.optionLabelFormatter) {
        const optionLabelFormatter =
          preparedProps.optionLabelFormatter as OptionLabelFormatterFunctionType;
        Object.assign(preparedProps, {
          options: preparedProps.options.map(option => ({
            ...option,
            label: optionLabelFormatter(option),
          })),
        });
        delete preparedProps.optionLabelFormatter;
      }
      if (Array.isArray(preparedProps.options)) {
        const filterSort = (preparedProps.filterSort ||
          sortCheckboxOptions) as typeof sortCheckboxOptions;
        Object.assign(preparedProps, {
          options: preparedProps.options.sort(filterSort),
        });
        delete preparedProps.filterSort;
      }
      if (Array.isArray(preparedProps.options) && !!preparedProps?.hideDisabledOptions) {
        Object.assign(preparedProps, {
          options: preparedProps.options.filter(filterDisabledOptions),
        });
        delete preparedProps.hideDisabledOptions;
      }
      return preparedProps;
    }
    case FilterBlocksEnum.FilterDuoColorPicker: {
      if (!preparedProps?.options && !!filterValues) {
        const resolvedValues = [filterValues].flat();
        Object.assign(preparedProps, {
          options: compactArray(
            resolvedValues.map(value => optionFormatterMap[type](value, setting)),
          ),
        });
      }
      if (Array.isArray(preparedProps.options)) {
        const filterSort = (preparedProps.filterSort ||
          sortCheckboxOptions) as typeof sortCheckboxOptions;
        Object.assign(preparedProps, {
          options: preparedProps.options.sort(filterSort),
        });
      }
      if (Array.isArray(preparedProps.options) && !!preparedProps?.hideDisabledOptions) {
        Object.assign(preparedProps, {
          options: preparedProps.options.filter(filterDisabledOptions),
        });
      }
      return preparedProps;
    }
    case FilterBlocksEnum.FilterSlider: {
      if (!preparedProps?.min || !preparedProps.max) {
        if (instanceOfFilterRange(filterValues)) {
          Object.assign(preparedProps, {
            min: preparedProps?.min || filterValues?.min,
            max: preparedProps?.max || filterValues?.max,
          });
        }
      }
      return preparedProps;
    }
    case FilterBlocksEnum.FilterRangeSlider: {
      if (!preparedProps?.min || !preparedProps.max) {
        if (instanceOfFilterRange(filterValues)) {
          Object.assign(preparedProps, {
            min: preparedProps?.min || filterValues?.min,
            max: preparedProps?.max || filterValues?.max,
          });
        }
      }
      return {
        minLabel: i18n.t('rangeSlider.from'),
        maxLabel: i18n.t('rangeSlider.to'),
        labelFormatter: (value: string | number) => i18n.t('currency', { value }),
        valueFormatter: (value?: string | number) =>
          `${value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`,
        valueParser: (value?: string) => {
          if (typeof value !== 'string') {
            return null;
          }
          return Number(value.replace(/\s/g, ''));
        },
        headerDisplayType: 'input',
        ...preparedProps,
      };
    }
    default:
      return preparedProps;
  }
};

export default prepareFilterItemProps;
